/**
 * Thread progress tracker for Radar.
 *
 * Persists implementation thread progress to localStorage so users can
 * track which steps they have completed across sessions.
 */

// ── Types ──────────────────────────────────────────────────────────────────────

export interface StepProgress {
  completed: boolean
  completedAt?: number
  verifiedScore?: number
  verifiedAt?: number
}

export interface ThreadProgress {
  domain: string
  threadId: string
  steps: Record<string, StepProgress>
  startedAt: number
  lastUpdatedAt: number
}

export type ProgressMap = Record<string, ThreadProgress>

// ── Storage ────────────────────────────────────────────────────────────────────

const STORAGE_PREFIX = 'pm_thread_progress_'

function storageKey(domain: string): string {
  return `${STORAGE_PREFIX}${domain.replace(/[^a-zA-Z0-9.-]/g, '_')}`
}

export function loadProgress(domain: string): ProgressMap {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(storageKey(domain))
    if (!raw) return {}
    return JSON.parse(raw) as ProgressMap
  } catch {
    return {}
  }
}

export function saveProgress(domain: string, map: ProgressMap): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(storageKey(domain), JSON.stringify(map))
  } catch {
    // localStorage full or unavailable
  }
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function toggleStepComplete(
  map: ProgressMap,
  domain: string,
  threadId: string,
  stepOrder: number
): ProgressMap {
  const now = Date.now()
  const existing = map[threadId] || {
    domain,
    threadId,
    steps: {},
    startedAt: now,
    lastUpdatedAt: now,
  }

  const stepKey = String(stepOrder)
  const current = existing.steps[stepKey]
  const wasCompleted = current?.completed || false

  return {
    ...map,
    [threadId]: {
      ...existing,
      lastUpdatedAt: now,
      steps: {
        ...existing.steps,
        [stepKey]: wasCompleted
          ? { completed: false }
          : { completed: true, completedAt: now },
      },
    },
  }
}

export function markStepVerified(
  map: ProgressMap,
  domain: string,
  threadId: string,
  stepOrder: number,
  score: number
): ProgressMap {
  const now = Date.now()
  const existing = map[threadId] || {
    domain,
    threadId,
    steps: {},
    startedAt: now,
    lastUpdatedAt: now,
  }

  const stepKey = String(stepOrder)
  const current = existing.steps[stepKey] || { completed: false }

  return {
    ...map,
    [threadId]: {
      ...existing,
      lastUpdatedAt: now,
      steps: {
        ...existing.steps,
        [stepKey]: {
          ...current,
          verifiedScore: score,
          verifiedAt: now,
        },
      },
    },
  }
}
